import { Controller } from "@hotwired/stimulus"

// Product-centric "Add product". Search and pick ONE product; a staging card
// opens with a price box for each of that product's grades (Regular, Specialty…)
// — grades already on the list show as a note instead of a box, so you can't
// double-add them. Type the prices you want and click "Add to list": only the
// boxes you filled in become rows in the table above (ordinary nested-form rows
// the bulk Save persists). Picking a product never adds anything on its own, and
// re-picking a fully-listed product tells you so instead of silently doing
// nothing. An optional colour selector stages per-colour overrides the same way.
export default class extends Controller {
  static targets = ["search", "results", "stage", "gradeTemplate", "plainTemplate"]
  static values = { products: Array, existing: Array }

  connect() {
    this.idx = Date.now()
    this.added = new Set(this.existingValue) // keys already on the list — never re-add
    this.staged = null
    this.tbody = document.querySelector("[data-nested-form-target='target']")
    // Collapse the results dropdown on an outside click (mousedown fires before
    // focus churn, so it's more reliable than click) or the Escape key.
    this.onAway = (e) => { if (!this.element.contains(e.target)) this.hideResults() }
    this.onKey = (e) => { if (e.key === "Escape") this.hideResults() }
    document.addEventListener("mousedown", this.onAway)
    this.element.addEventListener("keydown", this.onKey)
  }

  disconnect() {
    document.removeEventListener("mousedown", this.onAway)
    this.element.removeEventListener("keydown", this.onKey)
  }

  filter() {
    const q = this.searchTarget.value.trim().toLowerCase()
    // Only surface the list once you actually start searching — don't dump the
    // whole catalog (covering the rest of the page) the instant the box is focused.
    if (q === "") { this.hideResults(); return }
    const matches = this.productsValue.filter((p) =>
      p.name.toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q)
    ).slice(0, 50)
    this.resultsTarget.innerHTML = matches
      .map((p) => {
        // Two products can share a display name (e.g. an HTV "Glow in the Dark"
        // and a CUT-ONLY one). Show SKU · category so they're distinguishable.
        const meta = [p.sku, p.category].filter((s) => s && s.length).join(" · ")
        const metaHtml = meta ? `<span class="pl-cust-meta" title="${this.esc(meta)}">${this.esc(meta)}</span>` : ""
        return `<button type="button" class="pl-cust-option" data-action="product-pricer#pick" data-id="${p.id}"><span class="pl-cust-name">${this.esc(p.name)}</span>${metaHtml}</button>`
      })
      .join("")
    this.resultsTarget.hidden = matches.length === 0
  }

  // Pick a product: open the staging card. Nothing is added to the list yet.
  pick(event) {
    const p = this.productsValue.find((x) => x.id === event.currentTarget.dataset.id)
    if (!p) return
    this.staged = p
    this.searchTarget.value = ""
    this.hideResults()
    this.renderStage(p)
  }

  // Build the staging card: a $ box per not-yet-listed grade, a note for the
  // ones already on the list, an optional colour selector, and an Add button.
  renderStage(p) {
    const boxes = []
    const onList = []
    if (p.grades.length) {
      p.grades.forEach((g) => {
        if (this.added.has(`${p.id}|${g.id}`)) onList.push(g.name)
        else boxes.push(this.box(`grade:${g.id}`, `<span class="pl-chip">grade</span> ${this.esc(g.name)}`))
      })
    } else if (this.added.has(p.id)) {
      onList.push("this product")
    } else {
      boxes.push(this.box("whole", "One price"))
    }

    let colour = ""
    if (p.variants.length) {
      const opts = ['<option value="">+ price a single colour…</option>'].concat(
        p.variants.map((v) => `<option value="${v.id}|${this.esc(v.label)}">${this.esc(v.label)}</option>`)
      )
      colour = `<div class="pl-stage-colour"><select class="pl-select" data-action="product-pricer#addColour">${opts.join("")}</select><div data-pricer-colours></div></div>`
    }

    this.stageTarget.innerHTML =
      `<div class="pl-stage-head"><strong>${this.esc(p.name)}</strong>` +
      `<button type="button" class="pl-stage-x" data-action="product-pricer#clearStage">change product</button></div>` +
      (boxes.length ? `<div class="pl-stage-rows">${boxes.join("")}</div>` : "") +
      (onList.length ? `<p class="pl-stage-note">Already on this list: ${this.esc(onList.join(", "))}.</p>` : "") +
      colour +
      `<div class="pl-stage-foot">` +
      `<button type="button" class="btn btn-primary btn-xs" data-action="product-pricer#commit">Add to list</button>` +
      `<span class="pl-stage-msg" data-pricer-msg></span></div>`
    this.stageTarget.hidden = false
    const first = this.stageTarget.querySelector("input[data-pricer-key]")
    if (first) first.focus()
  }

  // A staging price box, tagged with the key its row will use on commit.
  box(key, labelHtml) {
    return `<label class="pl-stage-row"><span class="pl-stage-label">${labelHtml}</span>` +
      `<span class="pl-money"><span class="pl-money-sym">$</span>` +
      `<input type="number" min="0" step="0.01" class="pl-input pl-money-input" data-pricer-key="${key}" placeholder="0.00"></span></label>`
  }

  // Colour selector → stage one more priced box (for a single variant override).
  addColour(event) {
    const sel = event.currentTarget
    const raw = sel.value || ""
    sel.value = ""
    const cut = raw.indexOf("|")
    if (cut === -1) return
    const vid = raw.slice(0, cut)
    const vlabel = raw.slice(cut + 1)
    if (!vid) return
    const holder = this.stageTarget.querySelector("[data-pricer-colours]")
    if (!holder) return
    if (this.added.has(vid)) { this.flash(`${vlabel} is already on this list.`); return }
    if (holder.querySelector(`[data-pricer-key="variant:${vid}"]`)) return // already staged
    holder.insertAdjacentHTML("beforeend", this.box(`variant:${vid}`, `<span class="pl-chip">colour</span> ${this.esc(vlabel)}`))
    const box = holder.querySelector(`[data-pricer-key="variant:${vid}"]`)
    if (box) box.focus()
  }

  clearStage() {
    this.staged = null
    this.stageTarget.hidden = true
    this.stageTarget.innerHTML = ""
    this.searchTarget.focus()
  }

  // Add to list: every staged box with a price becomes a row; blanks are
  // skipped. If you click with nothing filled in, say so rather than no-op.
  commit() {
    const p = this.staged
    if (!p) return
    let added = 0
    this.stageTarget.querySelectorAll("input[data-pricer-key]").forEach((input) => {
      if (input.value.trim() === "") return
      const price = parseFloat(input.value)
      if (!(price >= 0)) return
      const key = input.dataset.pricerKey
      if (key === "whole") {
        this.appendRow(this.plainTemplateTarget, { PID: p.id, PNAME: p.name, SUB: "", PRICE: price })
        this.added.add(p.id)
      } else if (key.startsWith("grade:")) {
        const gid = key.slice(6)
        const g = p.grades.find((x) => x.id === gid)
        this.appendRow(this.gradeTemplateTarget, { PID: p.id, GID: gid, PNAME: p.name, GNAME: g ? g.name : "", PRICE: price })
        this.added.add(`${p.id}|${gid}`)
      } else if (key.startsWith("variant:")) {
        const vid = key.slice(8)
        const v = p.variants.find((x) => x.id === vid)
        this.appendRow(this.plainTemplateTarget, {
          PID: vid, PNAME: p.name, PRICE: price,
          SUB: `<span class="pl-chip">colour</span><span>${this.esc(v ? v.label : "")}</span>`
        })
        this.added.add(vid)
      }
      added++
    })
    if (added === 0) { this.flash("Enter a price in at least one box first."); return }
    this.clearStage()
  }

  flash(text) {
    const msg = this.stageTarget.querySelector("[data-pricer-msg]")
    if (msg) msg.textContent = text
  }

  appendRow(template, fields) {
    let html = template.innerHTML.replace(/__IDX__/g, this.idx++)
    Object.keys(fields).forEach((k) => {
      let val
      if (k === "SUB") val = fields[k]                          // trusted chip HTML
      else if (k === "PRICE") val = parseFloat(fields[k]).toFixed(2)
      else val = this.esc(String(fields[k]))
      html = html.replace(new RegExp(`__${k}__`, "g"), val)
    })
    this.tbody.insertAdjacentHTML("beforeend", html)
  }

  hideResults() { this.resultsTarget.hidden = true }
  esc(s) { const d = document.createElement("div"); d.textContent = s == null ? "" : s; return d.innerHTML }
}
